import * as R from 'ramda';
import {
  useMemo,
  useState,
  useEffect,
  useCallback,
} from 'react';
// constants
import { FIELD_NAME, FIELD_ORDER, FIELD_VALUE } from '../../../../constants';
// utilities
import * as U from '../../../../utilities';
// component report-format-panel
import { defaultSortFields } from './settings';
import { setEditReportFormProps } from '../field-list/hocs';
import {
  getAvailableForReportSort,
  mapFieldsWithSequenceFromIndex,
} from '../../../report-format-panel/helpers';
//////////////////////////////////////////////////

const withReportSortList = WrappedComponent => props => { // eslint-disable-line
  const updatedProps = useMemo(() => setEditReportFormProps(props), [props]);

  const { sortFields } = updatedProps;

  const { reportSortList, setReportSortList } = props;

  const [availableForSort, setAvailableForSort] = useState(R.or(getAvailableForReportSort(props, sortFields), []));

  useEffect(() => {
    if (U.isNilOrEmpty(sortFields)) return;

    if (U.deepEquals(sortFields, reportSortList)) return;

    setReportSortList(sortFields);

    setAvailableForSort(getAvailableForReportSort(props, sortFields));
  }, []); // eslint-disable-line

  const handleAddSortField = useCallback(() => {
    const newField = R.assoc('sequence', R.inc(R.length(reportSortList)), defaultSortFields);
    const fieldsUpdated = R.append(newField, reportSortList);

    setReportSortList(fieldsUpdated);

    setAvailableForSort(getAvailableForReportSort(props, fieldsUpdated));
  }, [props, reportSortList, setReportSortList]);

  const handleRemoveSortField = useCallback(filterIndex => {
    const fieldsUpdated = mapFieldsWithSequenceFromIndex(R.dissocPath([filterIndex], reportSortList));

    setReportSortList(fieldsUpdated);

    setAvailableForSort(getAvailableForReportSort(props, fieldsUpdated));
  }, [props, reportSortList, setReportSortList]);

  const handleSelectSortName = useCallback((values, fieldIndex) => {
    const name = R.pathOr('', [FIELD_NAME], values);
    const value = R.pathOr('', ['option', FIELD_VALUE], values);

    const newField = {
      ...defaultSortFields,
      sequence: fieldIndex,
      name: R.or(name, value),
    };

    const fieldsUpdated = R.assocPath([fieldIndex], newField, reportSortList);

    setReportSortList(fieldsUpdated);

    setAvailableForSort(getAvailableForReportSort(props, fieldsUpdated));
  }, [props, reportSortList, setReportSortList]);

  const handleChangeFieldOrder = useCallback(({ order, fieldIndex }) => {
    const fieldsUpdated = R.assocPath([fieldIndex, FIELD_ORDER], order, reportSortList);

    setReportSortList(fieldsUpdated);

    setAvailableForSort(getAvailableForReportSort(props, fieldsUpdated));
  }, [props, reportSortList, setReportSortList]);

  return (
    <WrappedComponent
      {...props}
      reportSortList={reportSortList}
      availableForSort={availableForSort}
      setReportSortList={setReportSortList}
      handleAddSortField={handleAddSortField}
      setAvailableForSort={setAvailableForSort}
      handleSelectSortName={handleSelectSortName}
      handleRemoveSortField={handleRemoveSortField}
      handleChangeFieldOrder={handleChangeFieldOrder}
    />
  );
};

export {
  withReportSortList,
};
